/**
 * The ≈₽ rate inputs behind the matrix's forecast totals — the pure half.
 *
 * No React and no storage here: `resolveForecastRates` says what the totals
 * use, `parseRate` says what counts as a rate, and the hook
 * (`use-forecast-rates.ts`) only wires them to state and localStorage.
 *
 * Rule of the whole feature: a rate comes from the user or from the loaded
 * payments, never from nowhere. Clearing an input is a decision, so that
 * currency's ₽ total degrades to "—" instead of silently falling back.
 */

/** The toolbar's rate inputs, in display order. */
export const FORECAST_RATE_CURRENCIES = ['USD', 'EUR'] as const;

export type ForecastRateCurrency = (typeof FORECAST_RATE_CURRENCIES)[number];

export const FORECAST_RATES_KEY = 'payments.forecastRates';

/** What the inputs display, as typed — `''` is an explicit clear, not "unset". */
export type StoredRateInput = Partial<Record<ForecastRateCurrency, string>>;

/** `0`/negative/garbage is the same as empty: no rate, no ₽. */
export function parseRate(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const parsed = Number(value.trim().replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * The rates the totals actually use: last-known first, then every currency the
 * user has touched. A typed-but-empty input *removes* the rate — that is the
 * "never invent one" rule applied to the one case where a fallback would
 * silently undo what the user just did.
 */
export function resolveForecastRates(
  input: StoredRateInput,
  lastKnown: Record<string, number>,
): Record<string, number> {
  const rates: Record<string, number> = { ...lastKnown };
  for (const currency of FORECAST_RATE_CURRENCIES) {
    if (input[currency] === undefined) continue;
    const parsed = parseRate(input[currency]);
    if (parsed === undefined) delete rates[currency];
    else rates[currency] = parsed;
  }
  return rates;
}

/**
 * What each input displays: what the user typed, else the last-known rate as a
 * plain string, else nothing (an empty input, and a "—" ₽ total with it).
 */
export function forecastRateInputs(
  input: StoredRateInput,
  lastKnown: Record<string, number>,
): Record<ForecastRateCurrency, string> {
  return Object.fromEntries(
    FORECAST_RATE_CURRENCIES.map((currency) => [
      currency,
      input[currency] ?? (lastKnown[currency] !== undefined ? String(lastKnown[currency]) : ''),
    ]),
  ) as Record<ForecastRateCurrency, string>;
}

/** Tolerant read of the stored inputs: junk, wrong types and no storage all read as "nothing typed". */
export function readStoredRateInput(storage: Pick<Storage, 'getItem'> | null, key = FORECAST_RATES_KEY): StoredRateInput {
  if (!storage) return {};
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result: StoredRateInput = {};
    for (const currency of FORECAST_RATE_CURRENCIES) {
      const value = (parsed as Record<string, unknown>)[currency];
      if (typeof value === 'string') result[currency] = value;
    }
    return result;
  } catch {
    return {};
  }
}
